import { useEffect, useState } from 'react'
import { api, ErroApi } from '../lib/api'
import './nova-unidade.css'

/**
 * Abrir outra unidade.
 *
 * ─── A ROTA EXISTIA HÁ DIAS E NÃO TINHA PORTA ─────────────────────
 *
 * `app.criar_unidade` nasceu na 0032 e `POST /v1/conta/unidade` na
 * mesma leva. Nenhuma tela chamava. Quem tinha duas clínicas só
 * conseguia isso por caminho que não existe no produto.
 *
 * ─── POR QUE ELA EXISTE SEPARADA DE `criar_conta` ─────────────────
 *
 * Porque `app.criar_conta` vinculava usuário EXISTENTE como DONO da
 * clínica nova — pré-registrar o e-mail de outra pessoa e esperar a
 * clínica real convidá-la entregava a conta dela. A 0032 fechou isso, e
 * o caso legítimo ("o gestor que abre a segunda unidade") passou a
 * viver aqui, onde a pessoa JÁ provou a senha.
 *
 * ─── O SLUG É UM ENDEREÇO PÚBLICO ─────────────────────────────────
 *
 * Ele vira `clinuma.com.br/agendar/<slug>` e é único no sistema
 * inteiro. Por isso o aviso na tela: depois de entregue a paciente,
 * trocar quebra o link — e a 0036 nem deixa, o `slug` ficou fora do
 * GRANT de UPDATE.
 */

/** Mesmo formato que a API exige: minúsculas, dígitos e hífen. */
const paraSlug = (v: string) =>
  v.toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)

export function NovaUnidade({ aoFechar, aoCriar }: {
  aoFechar(): void
  aoCriar(tenantId: string): void | Promise<void>
}) {
  const [nome, setNome] = useState('')
  const [slug, setSlug] = useState('')
  const [slugTocado, setSlugTocado] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    const noEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') aoFechar() }
    document.addEventListener('keydown', noEscape)
    return () => document.removeEventListener('keydown', noEscape)
  }, [aoFechar])

  /* O slug segue o nome até a pessoa mexer nele. Depois disso ela
     manda — sobrescrever o que alguém digitou é o tipo de "ajuda" que
     faz perder confiança na tela. */
  const mudarNome = (v: string) => {
    setNome(v)
    if (!slugTocado) setSlug(paraSlug(v))
  }

  const valido = nome.trim().length >= 2 && /^[a-z0-9][a-z0-9-]{2,39}$/.test(slug)

  async function criar(e: React.FormEvent) {
    e.preventDefault()
    if (!valido) return
    setSalvando(true); setErro(null)
    try {
      const r = await api<{ tenantId: string }>('/v1/conta/unidade', {
        metodo: 'POST',
        corpo: { clinica: nome.trim(), slug },
      })
      await aoCriar(r.tenantId)
    } catch (err) {
      setErro(
        err instanceof ErroApi
          ? (err.codigo === 'JA_EXISTE'
              ? 'Já existe uma clínica com esse endereço. Escolha outro.'
              : err.message)
          : 'Não foi possível abrir a unidade.',
      )
      setSalvando(false)
    }
  }

  return (
    <>
      <div className="painel__fundo" onClick={aoFechar} />
      <aside className="painel nun" role="dialog" aria-modal="true"
             aria-label="Abrir outra unidade">
        <header className="painel__topo">
          <h2>Abrir outra unidade</h2>
          <button className="botao botao--fantasma botao--pequeno" onClick={aoFechar}>
            Fechar
          </button>
        </header>

        <p className="ajuda nun__intro">
          A unidade nova nasce vazia e separada: agenda, pacientes,
          prontuário e caixa próprios. Você continua na mesma conta e
          troca entre elas pelo seletor no topo.
        </p>

        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

        <form className="nun__form" onSubmit={criar}>
          <div className="campo">
            <label htmlFor="nun-nome">Nome da unidade</label>
            <input id="nun-nome" className="entrada" autoFocus maxLength={120}
                   placeholder="Movimento — Zona Sul"
                   value={nome} onChange={(e) => mudarNome(e.target.value)} />
            <span className="ajuda">
              Use algo que diferencie das outras: o bairro, a rua, a cidade.
            </span>
          </div>

          <div className="campo">
            <label htmlFor="nun-slug">Endereço da página de agendamento</label>
            <div className="nun__slug">
              <span className="nun__slug-fixo">clinuma.com.br/agendar/</span>
              <input id="nun-slug" className="entrada" maxLength={40}
                     value={slug}
                     onChange={(e) => { setSlugTocado(true); setSlug(paraSlug(e.target.value)) }} />
            </div>
            <span className="ajuda">
              É público e único. Depois de entregue a paciente não dá para
              trocar — o link já estará em conversa de WhatsApp.
            </span>
          </div>

          <div className="nun__acoes">
            <button className="botao botao--fantasma" type="button" onClick={aoFechar}>
              Cancelar
            </button>
            <button className="botao" type="submit" disabled={!valido || salvando}>
              {salvando ? 'Abrindo…' : 'Abrir unidade'}
            </button>
          </div>
        </form>

        {/* A consequência de dinheiro, escrita ANTES. Cada unidade conta
            no teto do plano, e descobrir isso no 409 seguinte é a
            mensagem chegando tarde demais. */}
        <p className="ajuda nun__aviso">
          Cada unidade conta no limite do seu plano. Se o teto já estiver
          cheio, o sistema avisa e nada é criado.
        </p>
      </aside>
    </>
  )
}
